/**
 * Clinically-core optional nutrients (spec §1, §3) — port of mobile
 * services/liver/optionalNutrients.ts; keep in sync.
 *
 * Sodium (cirrhosis ± decompensated) and protein (cirrhosis ± decompensated /
 * alcohol-related) are clinical essentials, not preferences: their display
 * toggles are forced ON regardless of stored settings.
 */

import type { OptionalNutrients } from '@/types'

const SODIUM_CORE_CONDITIONS: readonly string[] = ['cirrhosis', 'cirrhosis_decompensated']
const PROTEIN_TARGET_CONDITIONS: readonly string[] = [
  'cirrhosis',
  'cirrhosis_decompensated',
  'alcohol_related',
]

export interface ClinicallyCoreNutrients {
  sodium: boolean
  protein: boolean
}

export function getClinicallyCoreNutrients(condition?: string | null): ClinicallyCoreNutrients {
  return {
    sodium: !!condition && SODIUM_CORE_CONDITIONS.includes(condition),
    protein: !!condition && PROTEIN_TARGET_CONDITIONS.includes(condition),
  }
}

/**
 * Merge stored preferences with the condition's clinical requirements.
 * Returns prefs unchanged for conditions with no clinically-core nutrients
 * (so `undefined` stays `undefined`).
 */
export function getEffectiveOptionalNutrients(
  condition?: string | null,
  prefs?: OptionalNutrients,
): OptionalNutrients | undefined {
  const core = getClinicallyCoreNutrients(condition)
  if (!core.sodium && !core.protein) return prefs
  return {
    ...prefs,
    showSodium: core.sodium || (prefs?.showSodium ?? false),
    showProtein: core.protein || (prefs?.showProtein ?? false),
  }
}
